/**
 * useIndicatorClassification - Auto-classifies indicators into chart panes
 *
 * WHY THIS EXISTS:
 * When RSI (0-100) and price (~6,800) share one Y-axis, both lines appear flat.
 * TradingView solves this with separate panes for different-scale indicators.
 * This hook auto-detects which indicators need separate panes based on data ranges,
 * with NO backend metadata changes required.
 *
 * ALGORITHM:
 * 1. Determine a "price reference" from OHLC data (or largest-median line indicator)
 * 2. For each line indicator, compute median and range from full dataset
 * 3. Compare each indicator's median to the price reference:
 *    - Median ratio < 0.1 or > 10.0 → different scale → separate pane
 *    - Range expansion ratio > 5.0 → different dynamics → separate pane
 * 4. Group similar-range separate indicators together (hierarchical clustering)
 * 5. Cap at MAX_PANES total (price + indicator panes)
 *
 * REFERENCE LINES:
 * For bounded oscillators, auto-detect and add reference lines:
 * - RSI-like (0-100): lines at 30 and 70
 * - MACD-like (oscillates around 0): line at 0
 */
import { useMemo } from 'react';

// Classification thresholds
const MEDIAN_RATIO_LOW = 0.1;      // Indicator median < 10% of price → separate
const MEDIAN_RATIO_HIGH = 10.0;    // Indicator median > 10x price → separate
const EXPANSION_THRESHOLD = 5.0;   // Indicator range/price range > 5x → separate
const GROUP_COMPATIBILITY = 2.0;   // Max ratio between medians for grouping together
const MAX_PANES = 3;               // Price pane + up to 2 indicator panes
const MAX_INDICATORS_PER_PANE = 4; // Don't overcrowd a single pane
const MIN_DATA_POINTS = 5;         // Need enough data to compute stats

/**
 * Calculate median of an array of numbers.
 * Uses quickselect-style approach for efficiency.
 */
function median(values) {
  if (!values || values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Calculate statistics for an indicator from chart data.
 * Returns { median, min, max, range, count }
 */
function calculateIndicatorStats(indicator, chartData) {
  const values = [];
  const name = indicator.name;

  for (const point of chartData) {
    const val = point[name];
    if (val !== undefined && val !== null && isFinite(val)) {
      values.push(val);
    }
  }

  if (values.length < MIN_DATA_POINTS) {
    return { median: 0, min: 0, max: 0, range: 0, count: 0, values: [] };
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  return {
    median: median(values),
    min,
    max,
    range: max - min,
    count: values.length,
    values,
  };
}

/**
 * Calculate price reference stats from OHLC data or line indicators.
 * Prefers OHLC close prices. Falls back to the indicator with the highest median.
 */
function calculatePriceReferenceStats(ohlcIndicators, lineIndicators, chartData) {
  // Try OHLC close prices first (most common case)
  for (const ohlc of ohlcIndicators) {
    const closeKey = `ohlc_${ohlc.name}_close`;
    const values = [];

    for (const point of chartData) {
      const val = point[closeKey];
      if (val !== undefined && val !== null && isFinite(val)) {
        values.push(val);
      }
    }

    if (values.length >= MIN_DATA_POINTS) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      return {
        median: median(values),
        min,
        max,
        range: max - min,
        count: values.length,
        source: 'ohlc',
        sourceName: ohlc.name,
      };
    }
  }

  // No OHLC data — use the line indicator with the highest absolute median
  // (price is typically the largest value)
  if (lineIndicators.length < 2) {
    // With 0-1 indicators, no classification is possible
    return null;
  }

  let bestStats = null;
  let bestMedian = -Infinity;

  for (const ind of lineIndicators) {
    const stats = calculateIndicatorStats(ind, chartData);
    if (stats.count >= MIN_DATA_POINTS && Math.abs(stats.median) > bestMedian) {
      bestMedian = Math.abs(stats.median);
      bestStats = { ...stats, source: 'line', sourceName: ind.name };
    }
  }

  return bestStats;
}

/**
 * Determine if an indicator should be in a separate pane from the price reference.
 *
 * Two-stage filter:
 * 1. Median ratio test: if the indicator's median is far from the price median
 * 2. Expansion ratio test: if the indicator's range is much larger than price range
 */
function shouldSeparate(indicatorStats, priceStats) {
  if (!indicatorStats || !priceStats || indicatorStats.count === 0 || priceStats.count === 0) {
    return false;
  }

  // Avoid division by zero
  const priceMedian = Math.abs(priceStats.median) || 1;
  const indicatorMedian = Math.abs(indicatorStats.median);

  // Stage 1: Median ratio test
  // If the indicator lives at a very different scale, separate it
  const medianRatio = indicatorMedian / priceMedian;
  if (medianRatio < MEDIAN_RATIO_LOW || medianRatio > MEDIAN_RATIO_HIGH) {
    return true;
  }

  // Stage 2: Expansion ratio test
  // Even if medians are similar, wildly larger ranges indicate different dynamics.
  // Low-range same-scale lines like moving averages and support/resistance belong on price.
  const priceRange = priceStats.range || 1;
  const indicatorRange = indicatorStats.range || 0;
  const expansionRatio = indicatorRange / priceRange;

  if (expansionRatio > EXPANSION_THRESHOLD) {
    return true;
  }

  return false;
}

/**
 * Group separate indicators into panes using simple hierarchical clustering.
 * Indicators with similar scales go into the same pane.
 * Returns an array of pane objects.
 *
 * Two-phase approach:
 * 1. Proactively merge compatible groups (close scales share a pane)
 * 2. Force-merge until at maxPanes cap (even if incompatible)
 */
function groupIntoPanes(separateIndicators, maxPanes) {
  if (separateIndicators.length === 0) return [];

  // Start with each indicator in its own group
  let groups = separateIndicators.map(item => ({
    indicators: [item.indicator],
    stats: [item.stats],
    medianCenter: Math.abs(item.stats.median) || 1,
  }));

  // Helper to merge group j into group i
  const mergeGroups = (i, j) => {
    groups[i].indicators.push(...groups[j].indicators);
    groups[i].stats.push(...groups[j].stats);
    // Recalculate center (geometric mean of medians)
    const allMedians = groups[i].stats.map(s => Math.abs(s.median) || 1);
    groups[i].medianCenter = Math.exp(
      allMedians.reduce((sum, m) => sum + Math.log(m), 0) / allMedians.length
    );
    groups.splice(j, 1);
  };

  // Phase 1: Proactively merge compatible groups (similar scales should share a pane)
  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < groups.length && !merged; i++) {
      for (let j = i + 1; j < groups.length && !merged; j++) {
        const ratio = groups[i].medianCenter / groups[j].medianCenter;
        const normalizedRatio = ratio > 1 ? ratio : 1 / ratio;
        if (normalizedRatio < GROUP_COMPATIBILITY) {
          mergeGroups(i, j);
          merged = true;
        }
      }
    }
  }

  // Phase 2: Force-merge until at maxPanes cap
  while (groups.length > maxPanes) {
    // Find the most compatible pair (closest median ratio)
    let bestI = 0;
    let bestJ = 1;
    let bestRatio = Infinity;

    for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        const ratio = groups[i].medianCenter / groups[j].medianCenter;
        const normalizedRatio = ratio > 1 ? ratio : 1 / ratio;
        if (normalizedRatio < bestRatio) {
          bestRatio = normalizedRatio;
          bestI = i;
          bestJ = j;
        }
      }
    }

    mergeGroups(bestI, bestJ);
  }

  // Enforce per-pane indicator limit
  // If a group has too many indicators, keep only the most important ones
  groups = groups.map(group => {
    if (group.indicators.length > MAX_INDICATORS_PER_PANE) {
      // Keep indicators with most data points (most reliable)
      const ranked = group.indicators
        .map((ind, i) => ({ ind, count: group.stats[i].count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, MAX_INDICATORS_PER_PANE);
      return {
        ...group,
        indicators: ranked.map(r => r.ind),
      };
    }
    return group;
  });

  return groups;
}

/**
 * Detect reference lines for bounded oscillators.
 * Returns an array of { y, label, color } objects.
 */
function detectReferenceLines(stats) {
  if (stats.count < MIN_DATA_POINTS || stats.range === 0) return [];

  const { min, max, median: med } = stats;
  const range = max - min;

  // RSI-like: bounded roughly 0-100, most values between 20-80
  if (min >= -5 && max <= 105 && range > 20 && range <= 110) {
    return [
      { y: 30, label: '30', color: 'rgba(34, 197, 94, 0.3)', strokeDasharray: '4 4' },
      { y: 70, label: '70', color: 'rgba(239, 68, 68, 0.3)', strokeDasharray: '4 4' },
    ];
  }

  // Stochastic-like: bounded 0-100 but with 20/80 levels
  // (Differentiate from RSI by checking if data clusters near extremes)

  // Oscillator around zero: MACD, CCI, momentum
  // Median close to zero relative to range
  if (Math.abs(med) < range * 0.15 && min < 0 && max > 0) {
    return [
      { y: 0, label: '0', color: 'rgba(136, 132, 216, 0.4)', strokeDasharray: '6 3' },
    ];
  }

  return [];
}

/**
 * Calculate per-pane Y-axis domain.
 */
function calculatePaneDomain(indicators, chartData, startIdx, endIdx) {
  const visibleData = chartData.slice(startIdx, endIdx + 1);
  let min = Infinity;
  let max = -Infinity;

  for (const point of visibleData) {
    for (const ind of indicators) {
      const val = point[ind.name];
      if (val !== undefined && val !== null && isFinite(val)) {
        min = Math.min(min, val);
        max = Math.max(max, val);
      }
    }
  }

  if (!isFinite(min) || !isFinite(max)) return [0, 100];

  const padding = (max - min) * 0.1 || 1;
  return [min - padding, max + padding];
}

/**
 * Hook: classifies indicators into chart panes.
 *
 * @param {Array} lineIndicators - All line indicators from indicatorData
 * @param {Array} ohlcIndicators - All OHLC indicators from indicatorData
 * @param {Array} chartData - Full unified chart data (timestamps + all indicator values)
 * @param {Object} zoomRange - { startIndex, endIndex } for visible data
 * @param {Set} hiddenIndicators - Set of hidden indicator names
 *
 * @returns {Object} {
 *   needsSeparatePanes: boolean,
 *   pricePaneIndicators: Array,    // Line indicators that overlay on price
 *   indicatorPanes: Array<{        // Separate indicator panes
 *     id: string,
 *     indicators: Array,
 *     referenceLines: Array,
 *     domain: [number, number],
 *     label: string,
 *   }>,
 *   totalPanes: number,
 * }
 */
export function useIndicatorClassification(
  lineIndicators,
  ohlcIndicators,
  chartData,
  zoomRange,
  hiddenIndicators
) {
  // Classification uses FULL data for stability (not affected by zoom level)
  const classification = useMemo(() => {
    // No line indicators → nothing to classify
    if (!lineIndicators || lineIndicators.length === 0 || !chartData || chartData.length === 0) {
      return {
        needsSeparatePanes: false,
        pricePaneIndicators: lineIndicators || [],
        indicatorPanes: [],
        totalPanes: 1,
      };
    }

    // Calculate price reference stats
    const priceStats = calculatePriceReferenceStats(ohlcIndicators, lineIndicators, chartData);

    if (!priceStats) {
      // Can't determine a price reference (0-1 indicators, no OHLC)
      return {
        needsSeparatePanes: false,
        pricePaneIndicators: lineIndicators,
        indicatorPanes: [],
        totalPanes: 1,
      };
    }

    // Classify each line indicator
    const overlay = [];
    const separate = [];

    lineIndicators.forEach(ind => {
      // If this is the "price reference" line indicator, keep it on price pane
      if (priceStats.source === 'line' && priceStats.sourceName === ind.name) {
        overlay.push(ind);
        return;
      }

      const stats = calculateIndicatorStats(ind, chartData);
      if (shouldSeparate(stats, priceStats)) {
        separate.push({ indicator: ind, stats });
      } else {
        overlay.push(ind);
      }
    });

    if (separate.length === 0) {
      return {
        needsSeparatePanes: false,
        pricePaneIndicators: lineIndicators,
        indicatorPanes: [],
        totalPanes: 1,
      };
    }

    // Group separate indicators into panes (cap at MAX_PANES - 1 for the price pane)
    const groups = groupIntoPanes(separate, MAX_PANES - 1);

    return {
      needsSeparatePanes: true,
      pricePaneIndicators: overlay,
      separateGroups: groups, // Raw groups for domain calculation
      totalPanes: 1 + groups.length,
    };
  }, [lineIndicators, ohlcIndicators, chartData]);

  // Per-pane domains recalculate on zoom (responsive to visible data)
  const indicatorPanes = useMemo(() => {
    if (!classification.needsSeparatePanes || !classification.separateGroups) {
      return [];
    }

    const startIdx = zoomRange?.startIndex ?? 0;
    const endIdx = zoomRange?.endIndex ?? (chartData?.length - 1) ?? 0;

    return classification.separateGroups.map((group, idx) => {
      // Calculate domain from visible data
      const domain = calculatePaneDomain(group.indicators, chartData, startIdx, endIdx);

      // Build label from indicator names
      const visibleInds = group.indicators.filter(ind => !hiddenIndicators?.has(ind.name));
      const label = visibleInds.map(ind => ind.name).join(' / ') || group.indicators[0]?.name || '';

      // Detect reference lines from the first indicator's full stats
      const firstStats = group.stats?.[0];
      const referenceLines = firstStats ? detectReferenceLines(firstStats) : [];

      // Check if all indicators in this pane are hidden → collapse
      const allHidden = group.indicators.every(ind => hiddenIndicators?.has(ind.name));

      return {
        id: `indicator-pane-${idx}`,
        indicators: group.indicators,
        referenceLines,
        domain,
        label,
        allHidden,
      };
    });
  }, [classification, zoomRange, chartData, hiddenIndicators]);

  return {
    needsSeparatePanes: classification.needsSeparatePanes,
    pricePaneIndicators: classification.pricePaneIndicators,
    indicatorPanes,
    totalPanes: classification.totalPanes,
  };
}

// Export internals for testing
export const _testExports = {
  median,
  calculateIndicatorStats,
  calculatePriceReferenceStats,
  shouldSeparate,
  groupIntoPanes,
  detectReferenceLines,
  calculatePaneDomain,
  MEDIAN_RATIO_LOW,
  MEDIAN_RATIO_HIGH,
  EXPANSION_THRESHOLD,
  GROUP_COMPATIBILITY,
  MAX_PANES,
  MAX_INDICATORS_PER_PANE,
};
